import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { HEALTHCOM_PRIMARY } from '../../constants/brand';
import { reportRequestSubmitError } from '../../services/reportError';
import requestService, {
  AuthRequestResponse,
  RequestBootstrap,
} from '../../services/request.service';
import { useLoadingOverlay } from '../common/LoadingOverlay';
import RequestAuth from './RequestAuth';
import RequestCodeInput from './RequestCodeInput';
import RequestRunner from './RequestRunner';
import StepMenu from './StepMenu';

// Stable identifier for a step. Used to apply the runner's local
// completion update back into the parent's `bootstrap.steps` without
// relying on a closure-captured index, which can drift when the parent
// re-renders mid-flow (the original index-based path silently no-op'd
// after a Continue click when the bootstrap reference had updated
// between renders — symptom: backend marked photo ID complete but the
// menu still read "Open" until a refresh).
import { RequestStep } from '@chiroup/core/types/Request.type';

const stepKey = (s: RequestStep): string => {
  if (s.patientSurveyId) return `survey:${s.patientSurveyId}`;
  if (s.patientConsentId) return `consent:${s.patientConsentId}`;
  if (s.patientFormId) return `form:${s.patientFormId}`;

  // basicInformation / insurance / uploadPhotoId / uploadInsuranceCard
  // are unique per request (at most one of each type), so `type` is a
  // stable key for them.
  return `type:${s.type}`;
};

type Methods = { phone?: string; email?: string; language?: 'en' | 'es' };

type Phase =
  | 'bootstrapping' // initial GET /requests/auth?requestId=...
  | 'methodSelect' // server returned available phone/email; ask user how to receive code
  | 'codeEntry' // method submitted; awaiting user-entered code
  | 'menu' // validation code accepted; show step picker
  | 'running' // a step from the menu is running
  | 'invalid'; // request id is bad / expired

const CODE_TIMEOUT_SECONDS = 300;

/**
 * `/requests/:id/*` shell — public entry point for the legacy patient
 * request flow, now living in healthcom-schedule. Owns the
 * validationCode auth state machine: ask the server which channels are
 * available, send a code on the user's pick, accept the code, then
 * mount the runner.
 *
 * The runner walks the request's steps using the same Soft Card panels
 * as the rest of healthcom-schedule.
 */
const PublicRequest: React.FC = () => {
  const { id: requestIdParam } = useParams<{ id: string }>();
  const requestId = requestIdParam || '';

  const [phase, setPhase] = useState<Phase>('bootstrapping');
  const [methods, setMethods] = useState<Methods>({});
  const [authResponse, setAuthResponse] = useState<string>('');
  const [bootstrap, setBootstrap] = useState<RequestBootstrap | null>(null);
  // Index into bootstrap.steps for the currently-running step. The
  // public flow runs one step at a time off the menu so the patient
  // can back out and pick a different item.
  const [activeStepIndex, setActiveStepIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidCode, setInvalidCode] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  // Clinic brand color, populated from the auth response. Sticks
  // across all phases so the colorization is consistent from the
  // first method-picker render through the runner.
  const [primaryColor, setPrimaryColor] = useState<string>(HEALTHCOM_PRIMARY);
  // Clinic Survey Preferences "Do not allow unrelated regions" — from
  // the auth response's clinicInfo, forwarded to the CCS body picker.
  const [doNotAllowUnrelatedRegions, setDoNotAllowUnrelatedRegions] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Apply server response — promotes phase based on what came back.
  const applyAuthResponse = useCallback(
    (res: AuthRequestResponse) => {
      const data = res?.data || {};

      if (data.clinicInfo?.primaryColor) {
        setPrimaryColor(data.clinicInfo.primaryColor);
      }

      if (data.clinicInfo?.doNotAllowUnrelatedRegions === true) {
        setDoNotAllowUnrelatedRegions(true);
      }

      // Server already had a validationCode for this requestId (e.g.
      // the user followed a deep link with the code in URL). Drop
      // the patient into the menu — they pick which item to work on
      // first instead of being force-marched through them.
      if (data.validationCode && data.steps) {
        setBootstrap({
          steps: data.steps,
          language: data.language ?? 'en',
          gender: data.gender as RequestBootstrap['gender'],
          age: data.age ?? 0,
          id: requestId,
          ccsFollowups: data.ccsFollowups,
          guardian: data.guardian,
          validationCode: data.validationCode,
          encounterId: data.encounterId,
          prMap: data.prMap,
          clinicId: (data as any).clinicId,
          smsConsent: (data as any).smsConsent,
          smsConsentMarketing: (data as any).smsConsentMarketing,
          complaints: (data as any).complaints,
        });
        setActiveStepIndex(null);
        setPhase('menu');

        return;
      }

      // Server says "I sent a code" — show the code-entry screen. Keyed off
      // the structured `codeSent` flag rather than the localized message,
      // which is why Spanish patients previously landed on the invalid screen.
      if (data.codeSent) {
        setAuthResponse(res.message ?? '');
        setTimeLeft(CODE_TIMEOUT_SECONDS);
        setPhase('codeEntry');

        return;
      }

      // Server returned available delivery channels — show method picker.
      if (data.phone || data.email) {
        setMethods({
          phone: data.phone,
          email: data.email,
          language: data.language ?? 'en',
        });
        setPhase('methodSelect');

        return;
      }

      // Nothing actionable came back — treat as invalid.
      setPhase('invalid');
    },
    [requestId]
  );

  // Bootstrap on mount.
  useEffect(() => {
    if (!requestId) {
      setPhase('invalid');

      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(null);

    requestService
      .authRequest({ requestId })
      .then(res => {
        if (cancelled) return;
        applyAuthResponse(res);
      })
      .catch(err => {
        if (cancelled) return;
        reportRequestSubmitError(err, { step: 'authRequest:bootstrap' });
        setError(err?.response?.data?.message || err?.message || 'Failed to load request.');
        setPhase('invalid');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [requestId, applyAuthResponse]);

  // Countdown timer for the code TTL.
  useEffect(() => {
    if (phase !== 'codeEntry' || timeLeft <= 0) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      return;
    }

    timerRef.current = setInterval(() => {
      setTimeLeft(t => Math.max(0, t - 1));
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [phase, timeLeft]);

  const submitMethod = async (method: 'text' | 'email') => {
    if (!requestId) return;

    setLoading(true);
    setError(null);
    setInvalidCode(false);

    try {
      const res = await requestService.authRequest({ requestId, method });
      applyAuthResponse(res);
    } catch (err: any) {
      reportRequestSubmitError(err, { step: 'authRequest:method' });
      setError(err?.response?.data?.message || err?.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (code: number) => {
    if (!requestId || !code) return;

    setLoading(true);
    setInvalidCode(false);

    try {
      const res = await requestService.authRequest({ requestId, validationCode: code });

      if (res?.data?.validationCode) {
        applyAuthResponse(res);
      } else {
        setInvalidCode(true);
      }
    } catch (err: any) {
      // A thrown code submission is almost always an expected outcome — an
      // expired/invalid code or a request that's no longer available, which
      // surfaces as a 4xx. Show the "invalid code" UI without polluting
      // Sentry. Only report genuinely unexpected failures (5xx, or a
      // transport/JS error with no HTTP status), and wrap them in a real
      // Error so Sentry groups them instead of logging the raw
      // `{ response: { data } }` object as "Object captured as exception".
      const statusCode = err?.response?.data?.statusCode;
      if (typeof statusCode !== 'number' || statusCode >= 500) {
        const message = err?.response?.data?.message || err?.message || 'Unknown error';
        Sentry.captureException(
          new Error(`submitCode failed (status ${statusCode ?? 'none'}): ${message}`)
        );
      }
      setInvalidCode(true);
    } finally {
      setLoading(false);
    }
  };

  const requestNewCode = () => {
    setAuthResponse('');
    setTimeLeft(0);
    setInvalidCode(false);
    setPhase('methodSelect');
  };

  const handleExit = () => {
    // Exit goes back to the homepage of the patient portal so the user
    // ends up somewhere coherent regardless of where they started.
    window.location.assign('/');
  };

  const updateBootstrap = (data: Partial<RequestBootstrap>) => {
    setBootstrap(prev => (prev ? { ...prev, ...data } : prev));
  };

  const openStep = (index: number) => {
    setActiveStepIndex(index);
    setPhase('running');
  };

  const exitStepToMenu = () => {
    setActiveStepIndex(null);
    setPhase('menu');
  };

  // -- Renders --

  // Loader visibility is handled by the app-level LoadingOverlay
  // provider (HealthcomLoadingPage) so the brand animation covers the
  // viewport during the initial bootstrap and any in-flight auth call
  // that doesn't have its own button-disabled state. When the
  // condition flips false, the overlay's settle/fade-out reveals the
  // correct phase below — no mid-load skeleton flash.
  useLoadingOverlay(
    phase === 'bootstrapping' || (loading && phase !== 'codeEntry' && phase !== 'methodSelect')
  );

  if (phase === 'bootstrapping' || (loading && phase !== 'codeEntry' && phase !== 'methodSelect')) {
    return null;
  }

  if (phase === 'invalid') {
    return (
      <main className="min-h-dvh flex flex-col items-center justify-center antialiased bg-gray-50 dark:bg-[var(--color-bg-page)] px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="w-full max-w-md">
          <div className="portal-card p-6 text-center">
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Request unavailable
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {error || 'This request link isn’t valid or has expired.'}
            </p>
          </div>
        </div>
      </main>
    );
  }

  if (phase === 'methodSelect') {
    return (
      <RequestAuth
        methods={methods}
        primaryColor={primaryColor}
        loading={loading}
        error={error}
        invalidCode={invalidCode}
        onSubmitMethod={submitMethod}
      />
    );
  }

  if (phase === 'codeEntry') {
    return (
      <RequestCodeInput
        authResponse={authResponse}
        onSubmitCode={submitCode}
        onHandleNewCode={requestNewCode}
        loading={loading}
        language={methods.language === 'es' ? 'es' : 'en'}
        invalidCode={invalidCode}
        timeLeft={timeLeft}
        primaryColor={primaryColor}
      />
    );
  }

  if (phase === 'menu' && bootstrap) {
    return (
      <main className="min-h-dvh flex flex-col items-center justify-center antialiased bg-gray-50 dark:bg-[var(--color-bg-page)] px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="w-full max-w-5xl">
          <StepMenu
            bootstrap={bootstrap}
            primaryColor={primaryColor}
            onPickStep={openStep}
            onFinish={handleExit}
          />
        </div>
      </main>
    );
  }

  if (phase === 'running' && bootstrap && activeStepIndex !== null) {
    const activeStep = bootstrap.steps[activeStepIndex];

    if (!activeStep) {
      // Index drifted (shouldn't normally happen) — bail to menu.
      exitStepToMenu();

      return null;
    }

    // Single-step bootstrap so RequestRunner only renders this one
    // step. When the step's "All set" panel triggers completion we
    // bounce back to the menu (handled by RequestRunner's
    // `singleStepMode` useEffect on `allDone`), where the just-
    // finished item now reads as Completed.
    const singleStepBootstrap: RequestBootstrap = {
      ...bootstrap,
      steps: [activeStep],
    };

    return (
      <RequestRunner
        bootstrap={singleStepBootstrap}
        primaryColor={primaryColor}
        doNotAllowUnrelatedRegions={doNotAllowUnrelatedRegions}
        singleStepMode
        onUpdateRequest={data => {
          if (!data.steps?.length) return;

          // Replace existing steps by key + append unseen ones. The
          // append is what carries backend-queued followups (Oswestry,
          // next ACCS) from the whole-survey submit into the menu.
          setBootstrap(prev => {
            if (!prev) return prev;

            const incomingByKey = new Map(data.steps!.map(s => [stepKey(s), s]));
            const refreshedExisting = prev.steps.map(s => incomingByKey.get(stepKey(s)) ?? s);
            const seenKeys = new Set(refreshedExisting.map(stepKey));
            const newOnes = data.steps!.filter(s => !seenKeys.has(stepKey(s)));

            return {
              ...prev,
              ...data,
              steps: [...refreshedExisting, ...newOnes],
            };
          });
        }}
        onExit={exitStepToMenu}
      />
    );
  }

  return null;
};

export default PublicRequest;
